import type { Feature, LineString, Polygon } from 'geojson';
import type { Boundary, LngLat } from '../../shared/protocol';
import { searchWindowsFor, type BarrierKind, type TravelMode } from '../../shared/travelMode';
import { enclosingBlock, type FaceFailure, type PlanarLine } from '../../shared/map/planarFace';
import { selectBoundary, targetAreaSqM, type ClassifiedBarrier, type TargetModel } from '../../shared/map/boundarySelector';
import { compositionNames, describeComposition, type NamedLine } from '../../shared/map/boundaryDescribe';

/**
 * v23 BOLT-124 / v25 BOLT-140 — the smart auto-boundary.
 *
 * Jake's manual method as an algorithm: centre on your location, let the
 * barriers around you (major roads, water edges, rail lines) enclose a block,
 * and make that block the play area.
 *
 * BOLT-140 REPLACED THE ALGORITHM. The old one clipped the barriers to a search
 * window, ADDED THE WINDOW RECTANGLE as four extra "barriers" so the graph would
 * close, and handed the lot to `turf.polygonize`. Jake, in the field: "it seems
 * to just arbitrarily square off to close out the area. Many borders seem to
 * have no relationship with any object." Those borders were our own rectangle.
 *
 * The replacement is in `shared/map/planarFace.ts`: node the barriers into a
 * planar graph and WALK the single face containing the player. Nothing is added
 * to close anything, so nothing fabricated can come back - and "you are not
 * enclosed here" becomes a sign test on the walk rather than a guess. This file
 * is now only the adapter: GeoJSON in, `Boundary` out.
 *
 * It runs on the SERVER (`auto_boundary`), so it must stay pure TypeScript with
 * no map, no DOM and no turf. It lives under `client/map/` for continuity with
 * the harvest that feeds it.
 */

/** A wall. Roads, rail lines, and water EDGES all become plain lines: a lake
 *  shore is just another line, which is why mixing area and line barriers needs
 *  no special case. */
export type BarrierLine = Feature<LineString>;

export interface AutoBoundaryResult {
  boundary: Boundary;
  areaSqMeters: number;
  /** Which search window produced it, in metres. Diagnostics. */
  radiusM: number;
  /** How many vertices the ring has, after the vertex-budget simplify. */
  vertices: number;
}

/** A square window of `radiusM` around the centre, as a bbox. Still used by the
 *  synthetic-lattice fallback and by the harvest's coverage widening. */
export function windowBbox(center: LngLat, radiusM: number): [number, number, number, number] {
  const dLat = radiusM / 111_320;
  const dLng = dLat / Math.max(0.1, Math.cos((center.lat * Math.PI) / 180));
  return [center.lng - dLng, center.lat - dLat, center.lng + dLng, center.lat + dLat];
}

/** The last failure reason, for the lobby's banner. Not on the wire. */
export type AutoBoundaryFailure = FaceFailure;

function toPlanar(barriers: readonly BarrierLine[]): PlanarLine[] {
  const out: PlanarLine[] = [];
  for (const b of barriers) {
    const c = b?.geometry?.coordinates;
    if (Array.isArray(c) && c.length >= 2) out.push({ coordinates: c });
  }
  return out;
}

/**
 * The whole algorithm. Returns null for EVERY failure mode, which the lobby
 * turns into the manual-draw prompt.
 *
 * A truthful null beats a plausible polygon: criterion 7 of BOLT-140, and the
 * reason there is no "widen until something turns up" escape hatch left.
 */
export function autoBoundary(
  barriers: readonly BarrierLine[],
  center: LngLat,
  mode: TravelMode,
): AutoBoundaryResult | null {
  const out = autoBoundaryOutcome(barriers, center, mode);
  return out.ok ? out.result : null;
}

/** The same call, keeping WHY it failed. */
export function autoBoundaryOutcome(
  barriers: readonly BarrierLine[],
  center: LngLat,
  mode: TravelMode,
): { ok: true; result: AutoBoundaryResult } | { ok: false; reason: AutoBoundaryFailure } {
  const outcome = enclosingBlock(toPlanar(barriers), center, { radii: searchWindowsFor(mode) });
  if (!outcome.ok) return { ok: false, reason: outcome.reason };
  return { ok: true, result: resultOf(outcome.block.ring, outcome.block.areaSqMeters, outcome.block.radiusM) };
}

function resultOf(ring: number[][], areaSqMeters: number, radiusM: number): AutoBoundaryResult {
  const geometry: Polygon = { type: 'Polygon', coordinates: [ring] };
  return {
    boundary: { type: 'Feature', properties: {}, geometry } as Boundary,
    areaSqMeters,
    radiusM,
    vertices: ring.length,
  };
}

/**
 * v25 BOLT-140 — the same call, but through the SELECTOR.
 *
 * `autoBoundaryOutcome` above takes one barrier set and returns its block; this
 * takes the whole classified harvest and lets the selector choose which barrier
 * set to use, from the mode's target size. The geometry underneath is
 * identical - `selectBoundary` calls `enclosingBlock` and nothing else - so
 * this is a policy layer, not a second algorithm.
 *
 * It lives on the client because the CLASSES do. The wire's `auto_boundary`
 * carries bare `number[][][]`, and v25 Phase 1 explicitly adds no protocol
 * surface, so the server cannot rebuild a tier ladder from what it receives.
 * The web client already computes its own boundary and sends `set_boundary`
 * (see `useAutoBoundary`), so nothing about who is authoritative changes.
 */
/**
 * v26 BOLT-147 — when your own tier encloses NOTHING, widen before giving up.
 *
 * Jake, standing outside a house on a lakeside street in Little Elm: no play
 * area at all, on foot or on bike. The place is obviously playable.
 *
 * The barrier tier was never the problem. `searchWindowsFor` caps foot at
 * 3200 m, and the tertiary-and-above ring around that house closes at 12800 m -
 * so the app reported `not-enclosed` about a ring that exists. Measured: 20 of
 * 27 acceptance cases unchanged, 7 rescued, 0 failures, and every rescue is
 * 100% within 8 m of a harvested barrier because these are genuine rings of
 * real roads, just big ones.
 *
 * Jake's rule for what to return: "a too large, but undivided play area would be
 * better than residential, woods, chopped up maps which seem super strange and
 * unplayable." So it returns ONE face, never a union.
 *
 * Note the tier that wins in practice is the FINEST one, not a coarser one -
 * more walls means more chances for a ring to close. It reads as "widen the
 * window, and if that fails drop to the finest tier".
 */
const FALLBACK_WINDOWS_M = [800, 1600, 3200, 6400, 12800] as const;
const FALLBACK_TIERS: TravelMode[] = ['foot', 'bike', 'car'];

function enclosureFallback(
  barriers: readonly ClassifiedBarrier[],
  center: LngLat,
  mode: TravelMode,
  opts: { headStartMs: number; model?: TargetModel; names?: readonly NamedLine[] },
): ReturnType<typeof autoBoundarySelected> | null {
  const target = targetAreaSqM(mode, opts.headStartMs, opts.model);
  let best: { out: ReturnType<typeof selectBoundary>; area: number } | null = null;
  for (const tier of FALLBACK_TIERS) {
    for (const win of FALLBACK_WINDOWS_M) {
      const got = selectBoundary(barriers, center, tier, {
        headStartMs: opts.headStartMs,
        model: opts.model,
        radii: [win],
      });
      if (!got.ok || !got.chosen.block) continue;
      const area = got.chosen.block.areaSqMeters;
      // Closest to the mode's OWN target, in log space, so half and double are
      // penalised alike and a 60x rescue never beats a 2x one.
      if (!best || Math.abs(Math.log(area / target)) < Math.abs(Math.log(best.area / target)))
        best = { out: got, area };
    }
  }
  if (!best || !best.out.ok) return null;
  const block = best.out.chosen.block!;
  const composition = opts.names?.length
    ? compositionNames(describeComposition(block.ring, opts.names, center))
    : [];
  return {
    ok: true,
    result: resultOf(block.ring, block.areaSqMeters, block.radiusM),
    barriers: best.out.chosen.barriers,
    targetSqMeters: best.out.targetSqM,
    // A fallback is by definition a size compromise: it is the honest shape of
    // the ground, and Jake's call is that it is presented like any other map.
    sizeCompromised: true,
    composition,
  };
}

export function autoBoundarySelected(
  barriers: readonly ClassifiedBarrier[],
  center: LngLat,
  mode: TravelMode,
  opts: { headStartMs: number; model?: TargetModel; names?: readonly NamedLine[] },
):
  | {
      ok: true;
      result: AutoBoundaryResult;
      barriers: BarrierKind[];
      targetSqMeters: number;
      sizeCompromised: boolean;
      /** v25 BOLT-144 — what the RETURNED ring is made of, biggest first. */
      composition: string[];
    }
  | { ok: false; reason: AutoBoundaryFailure } {
  const out = selectBoundary(barriers, center, mode, {
    headStartMs: opts.headStartMs,
    model: opts.model,
    radii: searchWindowsFor(mode),
  });
  // v26 BOLT-147: your own tier answered. Nothing below this line runs, so the
  // 20 of 27 acceptance cases that work today are untouched.
  if (!out.ok) {
    const rescued = enclosureFallback(barriers, center, mode, opts);
    if (rescued) return rescued;
    return { ok: false, reason: out.reason };
  }
  const block = out.chosen.block!;
  // Named off the ring we are about to return, never off the inputs that
  // produced it, so the label cannot describe a different map than the one the
  // player is looking at. Same call the server makes on the iOS path.
  const composition = opts.names?.length
    ? compositionNames(describeComposition(block.ring, opts.names, center))
    : [];
  return {
    ok: true,
    result: resultOf(block.ring, block.areaSqMeters, block.radiusM),
    barriers: out.chosen.barriers,
    targetSqMeters: out.targetSqM,
    sizeCompromised: out.sizeCompromised,
    composition,
  };
}
